import { Paragraph } from '@zillow/constellation';
import type { Offense } from '@zg-rentals/ts-capps-client';
import { InlineDd, InlineDt } from '../../BackgroundReportEvictions/EvictionSectionContent';
import { getCurrencyDisplay, getTitleCase } from '../../../../utils/formatters';

export function CriminalOffense({ offense }: { offense: Offense }) {
  const {
    caseType,
    description,
    offenseDate,
    offenseCode,
    sentenceLength,
    statute,
    suspendedSentence,
    filingDate,
    disposition,
    dispositionDate,
    convictionDate,
    convictionLocation,
    ncicCode,
    ncicCodeDescription,
    probationSentenceLength,
    amendedDisposition,
    courtName,
    plea,
    courtCosts,
    fines,
    warrantDate,
    warrantCounty,
    supervisionCounty,
    supervisionState,
    supervisionDate,
    commitmentLocation,
    commitmentCounty,
    commitmentState,
    commitmentDate,
    releaseDate,
    fbiNumber,
    bookingNumber,
    restitution,
    status,
  } = offense;
  return (
    <>
      <Paragraph fontColor="coolGray500" fontType="finePrintHeading" marginTop="sm" marginBottom="xs">
        Offense details
      </Paragraph>
      {caseType && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Type:</InlineDt> <InlineDd>{getTitleCase(caseType)}</InlineDd>
        </Paragraph>
      )}
      {description && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Description:</InlineDt> <InlineDd>{getTitleCase(description)}</InlineDd>
        </Paragraph>
      )}
      {offenseDate && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Offense date:</InlineDt> <InlineDd>{offenseDate}</InlineDd>
        </Paragraph>
      )}
      {offenseCode && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Offense code:</InlineDt> <InlineDd>{offenseCode}</InlineDd>
        </Paragraph>
      )}
      {sentenceLength && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Sentence length:</InlineDt> <InlineDd>{sentenceLength.toLowerCase()}</InlineDd>
        </Paragraph>
      )}
      {statute && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Statute:</InlineDt> <InlineDd>{statute}</InlineDd>
        </Paragraph>
      )}
      {suspendedSentence && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Suspended sentence:</InlineDt> <InlineDd>{suspendedSentence.toLowerCase()}</InlineDd>
        </Paragraph>
      )}
      {filingDate && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Charge filing date:</InlineDt> <InlineDd>{filingDate}</InlineDd>
        </Paragraph>
      )}
      {disposition && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Disposition:</InlineDt> <InlineDd>{disposition}</InlineDd>
        </Paragraph>
      )}
      {dispositionDate && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Disposition date:</InlineDt> <InlineDd>{dispositionDate}</InlineDd>
        </Paragraph>
      )}
      {convictionDate && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Conviction date:</InlineDt> <InlineDd>{convictionDate}</InlineDd>
        </Paragraph>
      )}
      {convictionLocation && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Conviction location:</InlineDt> <InlineDd>{convictionLocation}</InlineDd>
        </Paragraph>
      )}
      {ncicCode && (
        <Paragraph fontType="bodySmall">
          <InlineDt>NCIC code:</InlineDt> <InlineDd>{ncicCode}</InlineDd>
        </Paragraph>
      )}
      {ncicCodeDescription && (
        <Paragraph fontType="bodySmall">
          <InlineDt>NCIC code description:</InlineDt> <InlineDd>{ncicCodeDescription}</InlineDd>
        </Paragraph>
      )}
      {probationSentenceLength && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Probation sentence length:</InlineDt> <InlineDd>{probationSentenceLength.toLowerCase()}</InlineDd>
        </Paragraph>
      )}
      {amendedDisposition && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Amended disposition:</InlineDt> <InlineDd>{amendedDisposition}</InlineDd>
        </Paragraph>
      )}
      {courtName && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Court:</InlineDt> <InlineDd>{courtName}</InlineDd>
        </Paragraph>
      )}
      {plea && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Plea:</InlineDt> <InlineDd>{getTitleCase(plea)}</InlineDd>
        </Paragraph>
      )}
      {courtCosts && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Court costs:</InlineDt> <InlineDd>{getCurrencyDisplay(courtCosts)}</InlineDd>
        </Paragraph>
      )}
      {fines && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Fines:</InlineDt> <InlineDd>{getCurrencyDisplay(fines)}</InlineDd>
        </Paragraph>
      )}
      {warrantDate && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Warrant date:</InlineDt> <InlineDd>{warrantDate}</InlineDd>
        </Paragraph>
      )}
      {warrantCounty && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Warrant county:</InlineDt> <InlineDd>{getTitleCase(warrantCounty)}</InlineDd>
        </Paragraph>
      )}
      {supervisionCounty && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Supervision county:</InlineDt> <InlineDd>{getTitleCase(supervisionCounty)}</InlineDd>
        </Paragraph>
      )}
      {supervisionState && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Supervision state:</InlineDt> <InlineDd>{supervisionState}</InlineDd>
        </Paragraph>
      )}
      {supervisionDate && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Supervision date:</InlineDt> <InlineDd>{supervisionDate}</InlineDd>
        </Paragraph>
      )}
      {commitmentLocation && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Commitment location:</InlineDt> <InlineDd>{commitmentLocation}</InlineDd>
        </Paragraph>
      )}
      {commitmentCounty && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Commitment county:</InlineDt> <InlineDd>{getTitleCase(commitmentCounty)}</InlineDd>
        </Paragraph>
      )}
      {commitmentState && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Commitment state:</InlineDt> <InlineDd>{commitmentState}</InlineDd>
        </Paragraph>
      )}
      {commitmentDate && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Commitment date:</InlineDt> <InlineDd>{commitmentDate}</InlineDd>
        </Paragraph>
      )}
      {releaseDate && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Release date:</InlineDt> <InlineDd>{releaseDate}</InlineDd>
        </Paragraph>
      )}
      {fbiNumber && (
        <Paragraph fontType="bodySmall">
          <InlineDt>FBI number:</InlineDt> <InlineDd>{fbiNumber}</InlineDd>
        </Paragraph>
      )}
      {bookingNumber && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Booking number:</InlineDt> <InlineDd>{bookingNumber}</InlineDd>
        </Paragraph>
      )}
      {restitution && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Restitution:</InlineDt> <InlineDd>{getCurrencyDisplay(restitution)}</InlineDd>
        </Paragraph>
      )}
      {status && (
        <Paragraph fontType="bodySmall">
          <InlineDt>Status:</InlineDt>{' '}
          <InlineDd>{status.toLowerCase() === 'blank' ? null : getTitleCase(status)}</InlineDd>
        </Paragraph>
      )}
    </>
  );
}
